import { clsx } from 'clsx'
import { type FC } from 'react'

import {
  type OfferPriceV1,
  type PimProductWithVariantsV1,
  PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'
import { IconInfo } from '@redteclab/icons'

import { useOpenableControls } from '../../../../generic-hooks'
import { priceAccessorGetDisplayPrice } from '../../../model/priceAccessor/priceAccessorGetDisplayPrice'
import { productAccessorIsLifestyleProduct } from '../../../model/productAccessor/productAccessorIsLifestyleProduct'
import { productAccessorIsPrescriptionProduct } from '../../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { useProductPageContext } from '../../productPageContext'
import { ProductPrescriptionBonusDialog } from '../../ProductPrescriptionBonusDialog/ProductPrescriptionBonusDialog'
import { ProductPricingDialog } from '../../ProductPricingDialog'

type ProductPriceTransparencyIconProps = {
  className?: string
}

const showPricingInfoIconStatus = (
  product: PimProductWithVariantsV1,
  productPrescriptionPrice: OfferPriceV1 | null,
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum,
): boolean => {
  const isPrescriptionProduct = productAccessorIsPrescriptionProduct(product)
  const isLifestyleProduct: boolean = productAccessorIsLifestyleProduct(product)

  const hasFixedPriceDifference =
    productPrescriptionPrice?.fixedPriceDifference &&
    productPrescriptionPrice.fixedPriceDifference > 0

  const isOtxProductWithSelectedPrescription =
    currentPrescriptionType !==
      PrescriptionPrescriptionTypeEnum.NoPrescription &&
    [
      PrescriptionPrescriptionTypeEnum.FreePrescription,
      PrescriptionPrescriptionTypeEnum.InsurancePrescription,
      PrescriptionPrescriptionTypeEnum.PrivatePrescription,
    ].includes(currentPrescriptionType)

  if (!isPrescriptionProduct && !isOtxProductWithSelectedPrescription) {
    return false
  }

  if (isLifestyleProduct) {
    return true
  }

  switch (currentPrescriptionType) {
    case PrescriptionPrescriptionTypeEnum.FreePrescription:
    case PrescriptionPrescriptionTypeEnum.InsurancePrescription: {
      if (hasFixedPriceDifference) {
        return true
      }

      return false
    }
    case PrescriptionPrescriptionTypeEnum.PrivatePrescription: {
      return true
    }
    default: {
      return false
    }
  }
}
export const ProductPriceTransparencyIcon: FC<
  ProductPriceTransparencyIconProps
> = ({ className }) => {
  const { currentOffer, currentPrescriptionType, isMasterPage, product } =
    useProductPageContext()

  const modalControlsPricing = useOpenableControls()

  if (isMasterPage || !currentOffer) {
    return null
  }

  const productPrescriptionPrice = priceAccessorGetDisplayPrice(
    currentOffer,
    currentPrescriptionType,
  )

  if (
    !showPricingInfoIconStatus(
      product,
      productPrescriptionPrice,
      currentPrescriptionType,
    )
  ) {
    return null
  }

  return (
    <>
      <button
        className={clsx('flex items-center justify-center', className)}
        onClick={modalControlsPricing.handleOpen}
        type="button"
      >
        <IconInfo className="size-4" />
      </button>
      {currentPrescriptionType ===
      PrescriptionPrescriptionTypeEnum.PrivatePrescription ? (
        <ProductPrescriptionBonusDialog
          isOpen={modalControlsPricing.isOpen}
          onClose={modalControlsPricing.handleClose}
        />
      ) : (
        <ProductPricingDialog
          isOpen={modalControlsPricing.isOpen}
          onClose={modalControlsPricing.handleClose}
        />
      )}
    </>
  )
}
